/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import CustomerHeader from '../components/CustomerHeader';
import './ServiceProviderProfilePage.css';

interface ProviderProfile {
  id: string;
  name: string;
  category: string;
  rating: number;
  reviews: number;
  price: string;
  experience: string;
  location: string;
  distance: string;
  available: boolean;
  verified: boolean;
  image?: string;
  skills: string[];
  languages: string[];
  responseTime: string;
  description: string;
  photos: string[];
  availability: {
    today: boolean;
    tomorrow: boolean;
    slots: string[];
  };
  phone: string;
  whatsapp: string;
}

const ServiceProviderProfilePage: React.FC = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });
  const [provider, setProvider] = useState<ProviderProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProvider = async () => {
      if (!providerId) return;
      
      setLoading(true);
      try {
        const providerData = await apiService.getServiceProvider(providerId);
        setProvider(providerData);
      } catch (error: any) {
        console.error('Error loading provider:', error);
        // Provider will remain null, error state will be shown
      } finally {
        setLoading(false);
      }
    };

    loadProvider();
  }, [providerId]);

  const handleBookNow = () => {
    navigate(`/services/book/${providerId}`);
  };

  const handleCall = () => {
    window.open(`tel:${provider?.phone}`, '_self');
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Hello, I need ${provider?.category} service.`);
    window.open(`https://wa.me/${provider?.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <>
        <CustomerHeader
          variant="sticky"
          showTagline
          showVoiceSearch
          showProfileDropdown
          showBackButton
          onBackClick={() => navigate(-1)}
          onSearch={(query) => {
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          onCartClick={() => navigate('/cart')}
        />
        <div className="provider-profile-page">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{language === 'hi' ? 'लोड हो रहा है...' : 'Loading profile...'}</p>
          </div>
        </div>
      </>
    );
  }

  if (!provider) {
    return (
      <>
        <CustomerHeader
          variant="sticky"
          showTagline
          showVoiceSearch
          showProfileDropdown
          showBackButton
          onBackClick={() => navigate('/services')}
          onSearch={(query) => {
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          onCartClick={() => navigate('/cart')}
        />
        <div className="provider-profile-page">
          <div className="error-state">
            <p>{language === 'hi' ? 'प्रदाता नहीं मिला' : 'Provider not found'}</p>
            <button onClick={() => navigate('/services')}>
              {language === 'hi' ? 'वापस जाएं' : 'Go Back'}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton={true}
        onBackClick={() => navigate('/services')}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />
      <div className="provider-profile-page">

      {/* Main Content */}
      <main className="provider-profile-main">
        {/* Provider Header Card */}
        <section className="provider-header-card">
          <div className="provider-avatar-large">
            {provider.image ? (
              <img src={provider.image} alt={provider.name} />
            ) : (
              <span className="avatar-initial-large">
                {provider.name.charAt(0)}
              </span>
            )}
            {provider.verified && (
              <span className="verified-badge-large" title="Verified">✓</span>
            )}
          </div>
          <div className="provider-header-info">
            <h2 className="provider-name-large">{provider.name}</h2>
            <div className="provider-rating-large">
              <span className="rating-stars">⭐</span>
              <span className="rating-value">{provider.rating}</span>
              <span className="rating-reviews">
                ({provider.reviews} {language === 'hi' ? 'समीक्षा' : 'reviews'})
              </span>
            </div>
            <div className="provider-location">
              📍 {provider.location} • {provider.distance}
            </div>
            <div className="provider-experience">
              💼 {provider.experience} {language === 'hi' ? 'अनुभव' : 'experience'}
            </div>
            {!provider.available && (
              <span className="unavailable-badge-large">
                {language === 'hi' ? 'अभी उपलब्ध नहीं' : 'Currently Unavailable'}
              </span>
            )}
          </div>
        </section>

        {/* Quick Actions */}
        <section className="quick-actions-section">
          <button
            className="action-btn call-btn"
            onClick={handleCall}
          >
            📞 {language === 'hi' ? 'कॉल करें' : 'Call'}
          </button>
          <button
            className="action-btn whatsapp-btn"
            onClick={handleWhatsApp}
          >
            💬 {language === 'hi' ? 'WhatsApp' : 'WhatsApp'}
          </button>
          <button
            className="action-btn book-btn"
            onClick={handleBookNow}
            disabled={!provider.available}
          >
            {language === 'hi' ? 'बुक करें' : 'Book Now'}
          </button>
        </section>

        {/* Details Sections */}
        <section className="provider-details-section">
          <h3 className="section-title">
            {language === 'hi' ? 'कीमत' : 'Pricing'}
          </h3>
          <div className="pricing-card">
            <div className="pricing-info">
              <span className="price-label">{language === 'hi' ? 'कीमत:' : 'Price:'}</span>
              <span className="price-value-large">{provider.price}</span>
            </div>
            <div className="response-time-info">
              ⏱️ {language === 'hi' ? 'प्रतिक्रिया समय:' : 'Response Time:'} {provider.responseTime}
            </div>
          </div>
        </section>

        <section className="provider-details-section">
          <h3 className="section-title">
            {language === 'hi' ? 'कौशल' : 'Skills & Services'}
          </h3>
          <div className="skills-grid">
            {provider.skills.map((skill, idx) => (
              <span key={idx} className="skill-tag-large">
                {skill}
              </span>
            ))}
          </div>
        </section>

        <section className="provider-details-section">
          <h3 className="section-title">
            {language === 'hi' ? 'विवरण' : 'Description'}
          </h3>
          <p className="provider-description">{provider.description}</p>
        </section>

        <section className="provider-details-section">
          <h3 className="section-title">
            {language === 'hi' ? 'उपलब्धता' : 'Availability'}
          </h3>
          <div className="availability-card">
            <div className="availability-day">
              <span className="day-label">{language === 'hi' ? 'आज' : 'Today'}</span>
              <span className={`day-status ${provider.availability.today ? 'available' : 'unavailable'}`}>
                {provider.availability.today 
                  ? (language === 'hi' ? 'उपलब्ध' : 'Available')
                  : (language === 'hi' ? 'अनुपलब्ध' : 'Unavailable')
                }
              </span>
            </div>
            <div className="availability-day">
              <span className="day-label">{language === 'hi' ? 'कल' : 'Tomorrow'}</span>
              <span className={`day-status ${provider.availability.tomorrow ? 'available' : 'unavailable'}`}>
                {provider.availability.tomorrow 
                  ? (language === 'hi' ? 'उपलब्ध' : 'Available')
                  : (language === 'hi' ? 'अनुपलब्ध' : 'Unavailable')
                }
              </span>
            </div>
            {provider.availability.slots.length > 0 && (
              <div className="time-slots">
                <span className="slots-label">
                  {language === 'hi' ? 'समय स्लॉट:' : 'Time Slots:'}
                </span>
                <div className="slots-list">
                  {provider.availability.slots.map((slot, idx) => (
                    <span key={idx} className="slot-tag">{slot}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="provider-details-section">
          <h3 className="section-title">
            {language === 'hi' ? 'भाषाएं' : 'Languages'}
          </h3>
          <div className="languages-list">
            {provider.languages.map((lang, idx) => (
              <span key={idx} className="language-tag">{lang}</span>
            ))}
          </div>
        </section>
      </main>
      </div>
    </>
  );
};

export default ServiceProviderProfilePage;








